import Vue from 'vue'
import UserApi from '../../apis/UserApi'
import UserEvent from '../../objects/UserEvent'
import { getPageLoadExperimentValue, initializeStatsig } from '../../libraries/statsig'
import { capitalizeFirstLetter } from '../../helpers/stringUtils'
import { isUserOnMobile } from '../../utils/device-helper'
import { entityTypeFromBrand } from '../../utils/brand-helper'

export const namespaced = true

export const state = {
    flashcardTitle: 'Log in to publish these flashcards',
    loading: true,
    abGroup: 0,
    fillInTheBlankInstructionText: "Write <b>full sentence</b>. Don't skip any words.",
    fillInTheBlankPlaceholderText: 'Write your complete sentence.',
    hasNumberOfLinesTest: false,
    handwritingFontNames: {
        printFontName: 'Classic Print',
        cursiveFontName: 'Classic Cursive',
        groupOneHeaderName: 'Handwriting Fonts',
        groupTwoHeaderName: 'Other Fonts',
    },
    multipleChoicePlaceholder: {
        default: 'Option',
        add: 'Add Option',
    },

    // Page load experiments
    flashcardDefaultTitle: '',
    worksheetHeaderAndTitleLabelCombination: {
        id: 1,
        header: 'Worksheet Header',
        title: 'Worksheet Title',
    },
    hideInputFocusStyle: false,
    textFocusColor: '--color-primary-rgba-25',
    subscriptionSuspendOverlayCancelButtonColor: 'danger',
    hasCheckboxStyleTest: true,
    backgroundColor: '--background-color-default',
    addWidgetBackgroundColor: 'current',
    isInputSelectColorDefault: true,
    trashCanHasCurrentStyle: true,
    defaultFont: 'Open Sans',
    roleAndMarketingCollection: 'current',
}

export const mutations = {
    SET_VALUE(state, values) {
        Object.keys(values).forEach((value) => {
            state[value] = values[value]
        })
    },
    SET_AB_GROUP(state, group) {
        state.abGroup = group
    },
}

export const actions = {
    async initialize({ dispatch, rootGetters }, payload = {}) {
        try {
            state.loading = true
            await initializeStatsig()

            if (payload.completed) payload.completed()

            dispatch('initialisePageLoadExperiments')

            // Global experiments that need extra processing.
            // TODO: Maybe move these out of the module
            dispatch('setDefaultFontTestValues')

            dispatch('logStatsigEvent', { event: UserEvent.USER_VISIT })

            const isAuthtenticatedUser = rootGetters['user/isLoggedIn']
            const userHasTrialed = rootGetters['user/hasTrialed']
            const userIsOnFreePlan = rootGetters['user/isOnFreePlan']

            if (
                window?.new_browser &&
                !isAuthtenticatedUser &&
                (!document.referrer ||
                    !document.referrer.includes('myworksheetmaker') ||
                    !document.referrer.includes('brightsprout'))
            ) {
                // TODO: deprecate this event.
                dispatch('logStatsigEvent', { event: UserEvent.NEW_USER_VISIT })
            } else if (isAuthtenticatedUser) {
                await dispatch('loadAbGroup')
            }

            // Load experiments that are only for free plan users who have trialed (cardless).
            // TODO: Refactor this to use a more robust solution.
            if (isAuthtenticatedUser && userHasTrialed && userIsOnFreePlan) {
                await dispatch('user/getPayments', null, { root: true })

                // TODO: Look at moving this out of the abtests module.
                await dispatch('CardsModule/getPaymentCards', null, { root: true })

                const userHasMadePayments = rootGetters['user/getPayments']

                // TODO: Check this works. Look at moving this out of the abtests module.
                const userHasDefaultCard = !!rootGetters['CardsModule/defaultCardId']

                if (userHasMadePayments.length > 0 || userHasDefaultCard) {
                    return
                }

                dispatch('loadFreeTrialExpiredExperiments')
            }
        } catch (error) {
            console.error('Error initializing Statsig', error)
        } finally {
            state.loading = false
        }
    },
    logStatsigEvent({ _ }, { event, value, payload }) {
        Vue.prototype.$logTrackingEvent(event, value, false, false, payload)
    },
    async loadAbGroup({ commit }) {
        try {
            const response = await UserApi.getAbGroup()
            commit('SET_AB_GROUP', response.group)
        } catch (error) {
            console.error('Error getting AB group', error)
        }
    },
    /**
     * Initialises page load experiments from the window object.
     */
    initialisePageLoadExperiments({ commit }) {
        commit('SET_VALUE', {
            flashcardDefaultTitle: getPageLoadExperimentValue('flashcards_default_title_option', 'title', ''),
            worksheetHeaderAndTitleLabelCombination: getPageLoadExperimentValue(
                'worksheet_header_and_title_label_test',
                'titleHeaderLabelCombinationId',
                {
                    id: 1,
                    header: 'Worksheet Header',
                    title: 'Worksheet Title',
                },
            ),
            hideInputFocusStyle: getPageLoadExperimentValue('hide_input_focus_on_click', 'hide_focus_style', false),
            textFocusColor: getPageLoadExperimentValue('text_focus_color', 'color', '--color-primary-rgba-25'),
            subscriptionSuspendOverlayCancelButtonColor: getPageLoadExperimentValue(
                'subscription_suspend_overlay_cancel_button_color',
                'color',
                'danger',
            ),
            hasCheckboxStyleTest: getPageLoadExperimentValue('worksheet_header_checkbox_style', 'isSwitch', true),
            backgroundColor: getPageLoadExperimentValue('background_color', 'backgroundColor', '--background-color-default'),
            addWidgetBackgroundColor: getPageLoadExperimentValue('add_widget_background_color', 'headerStyle', 'current'),
            isInputSelectColorDefault: getPageLoadExperimentValue(
                `${window.doc?.entity_type}_input_select_text_color`,
                'isSystemDefaultColor',
                true,
            ),
            trashCanHasCurrentStyle: getPageLoadExperimentValue('student_info_trash_can_style', 'hasCurrentStyle', true),
            defaultFont: getPageLoadExperimentValue(
                `${capitalizeFirstLetter(window.doc?.entity_type)} Default Font`,
                'defaultFont',
                'Open Sans',
            ),
            roleAndMarketingCollection: getPageLoadExperimentValue(
                `${entityTypeFromBrand()}_role_and_marketing_collection`,
                'collectionMethod',
                'current',
            ),
        })
    },
    loadFreeTrialExpiredExperiments({ commit }) {
        // Leave empty until we have experiments to load.
        return
    },
    loadDomainCreateAccountHeaderTests({ commit }) {
        switch (window.doc?.entity_type) {
            case 'flashcard':
                commit('SET_VALUE', {
                    flashcardTitle: window.statsig
                        ?.getExperiment('flashcards_create_account_header_text', { disableExposureLog: !window.new_browser })
                        .get('title', 'Log in to publish these flashcardscards'),
                })
                break
            default:
                break
        }
    },
    loadFillInTheBlankTests({ commit, rootGetters }) {
        if (rootGetters['document/entityType'] !== 'worksheet') return

        commit('SET_VALUE', {
            fillInTheBlankInstructionText: window.statsig
                ?.getExperiment('fill_in_the_blank_instruction_text', { disableExposureLog: !window.new_browser })
                .get('text', state.fillInTheBlankInstructionText),
        })

        commit('SET_VALUE', {
            fillInTheBlankPlaceholderText: window.statsig
                ?.getExperiment('fill_in_the_blank_placholder_text', { disableExposureLog: !window.new_browser })
                .get('text', state.fillInTheBlankPlaceholderText),
        })
    },
    async loadOpenResponseNumberOfLinesTests({ commit, rootGetters }) {
        let entityType = rootGetters['document/entityType']
        if (!entityType || entityType !== 'worksheet' || (await isUserOnMobile())) return

        const testVariant = window.statsig?.getExperiment('open_response_number_of_lines', {
            disableExposureLog: !window.new_browser,
        })

        if (!testVariant || !testVariant.value) return

        const testVariantValue = testVariant.value.isSelector

        commit('SET_VALUE', {
            hasNumberOfLinesTest: testVariantValue,
        })
    },
    async loadHandwritingFontNamesTests({ commit, state, rootGetters }) {
        let entityType = rootGetters['document/entityType']
        if (!entityType || entityType !== 'worksheet' || (await isUserOnMobile())) return

        const testVariant = window.statsig
            ?.getExperiment('handwriting_font_names', { disableExposureLog: !window.new_browser })
            .get('font_and_header_name', state.handwritingFontNames)

        if (!testVariant) return

        commit('SET_VALUE', {
            handwritingFontNames: testVariant,
        })
    },
    async loadMultipleChoicePlaceholderTests({ commit, state, rootGetters }) {
        // Worksheets only. Do not expose experiment to mobile users.
        const entityType = rootGetters['document/entityType']
        if (!entityType || entityType !== 'worksheet' || (await isUserOnMobile())) {
            return
        }

        const testVariant = window.statsig?.getExperiment('Multiple Choice Placeholder', {
            disableExposureLog: !window.new_browser,
        })

        let testVariantValue
        if (!testVariant || !testVariant.value || !testVariant.value.add || !testVariant.value.default) {
            testVariantValue = state.multipleChoicePlaceholder
        } else {
            testVariantValue = testVariant.value
        }

        commit('SET_VALUE', {
            multipleChoicePlaceholder: testVariantValue,
        })
    },

    // TODO: Move out of this module.
    async setDefaultFontTestValues({ commit, dispatch, rootGetters, rootState, state }) {
        const entityType = rootGetters['document/entityType']
        if (!entityType || entityType === 'flashcard') return

        const document = rootState['document']

        const fontName = state.defaultFont

        if ((document && document.id) || !fontName) return

        await dispatch('document/setDocumentFont', { field: 'title_font', font: fontName }, { root: true })
        await dispatch('document/setDocumentFont', { field: 'student_info_font', font: fontName }, { root: true })
        await dispatch('document/setDocumentFont', { field: 'instructions_font', font: fontName }, { root: true })
        await dispatch('document/setDocumentFont', { field: 'body_font', font: fontName }, { root: true })
        await dispatch('document/setDocumentFont', { field: 'others', font: fontName }, { root: true })
    },
}

export const getters = {
    abGroup: (state) => state.abGroup,
    flashcardTitle: (state) => state.flashcardTitle,
    loading: (state) => state.loading,
    fillInTheBlankInstructionText: (state) => state.fillInTheBlankInstructionText,
    fillInTheBlankPlaceholderText: (state) => state.fillInTheBlankPlaceholderText,
    worksheetHeaderAndTitleLabelCombination: (state) => state.worksheetHeaderAndTitleLabelCombination,
    flashcardDefaultTitle: (state) => state.flashcardDefaultTitle,
    hideInputFocusStyle: (state) => state.hideInputFocusStyle,
    textFocusColor: (state) => state.textFocusColor,
    subscriptionSuspendOverlayCancelButtonColor: (state) => state.subscriptionSuspendOverlayCancelButtonColor,
    hasCheckboxStyleTest: (state) => state.hasCheckboxStyleTest,
    hasNumberOfLinesTest: (state) => state.hasNumberOfLinesTest,
    backgroundColor: (state) => state.backgroundColor,
    addWidgetBackgroundColor: (state) => state.addWidgetBackgroundColor,
    isInputSelectColorDefault: (state) => state.isInputSelectColorDefault,
    trashCanHasCurrentStyle: (state) => state.trashCanHasCurrentStyle,
    defaultFont: (state) => state.defaultFont,
    handwritingFontNames: (state) => state.handwritingFontNames,
    multipleChoicePlaceholder: (state) => state.multipleChoicePlaceholder,
    roleAndMarketingCollection: (state) => state.roleAndMarketingCollection,
}
